import { gql } from "@apollo/client";
import type { ResourceAttributeItem } from "./ResourceDetailsPane/types";

export const GET_RESOURCES = gql`
  query get_resources_ch(
    $first: Int
    $after: String
    $before: String
    $last: Int
    $filters_config: FiltersConfigInput
    $sort: [ResourcesSortBy]
  ) {
    get_resources: get_resources_ch(
      first: $first
      last: $last
      after: $after
      before: $before
      filters_config: $filters_config
      sort: $sort
    ) {
      pageInfo {
        hasNextPage
        endCursor
        startCursor
      }
      edges {
        node {
          cloud_account
          id
          datasource_tags_values
          datasources_from_resource_tags
          seem_tags_values
          resource_id
          cloud_provider
          cloud_account_friendly_name
          resource_name
          resource_type
          findings_count
          critical_findings_count
          open_raw_findings_count
          seem_tags_key_values
          tags_key_values
          resource_attributes {
            key
            value
            data_type
          }
        }
      }
    }
  }
`;

//TODO: not used yet
// export const GET_DATA_SOURCES_COUNT = gql`
//   query data_sources_count($filters_config: FiltersConfigInput) {
//     data_sources_count(filters_config: $filters_config) {
//       total_count
//     }
//   }
// `;

export const GET_RESOURCES_COUNT = gql`
  query resources_count(
    $filters_config: FiltersConfigInput
    $filters_context: FindingFiltersDataContext
  ) {
    resources_count(
      filters_config: $filters_config
      filters_context: $filters_context
    ) {
      total_count
    }
  }
`;

/**
 * Resource attributes for a single resource, looked up by its unique
 * `resource_id`. Deliberately minimal: the full `GET_RESOURCES` above also
 * selects `findings_count`, `critical_findings_count` and
 * `open_raw_findings_count`, aggregate counts a caller that only wants
 * attributes should not pay for.
 *
 * `resource_attributes` lives on `CHResource`, which `get_resources_ch` returns.
 * It is absent from the `resources` field, which resolves to
 * `PlainResourceNode` — that table has no attribute columns.
 *
 * The field comes back `null`, not `[]`, for a resource that has none: the
 * resolver only assigns it when at least one resource in the page had
 * attributes. Treat null and [] alike.
 */
export const GET_RESOURCE_ATTRIBUTES = gql`
  query resource_attributes($filters_config: FiltersConfigInput) {
    get_resources_ch(first: 1, filters_config: $filters_config) {
      edges {
        node {
          id
          resource_attributes {
            key
            value
            data_type
          }
        }
      }
    }
  }
`;

// --- GET_RESOURCE_ATTRIBUTES types ---

export interface GetResourceAttributesData {
  get_resources_ch?: {
    edges?: Array<{
      node?: {
        id?: string;
        resource_attributes?: ResourceAttributeItem[] | null;
      };
    }> | null;
  } | null;
}

// --- GET_RESOURCES types ---

export interface ResourcesFiltersConfigInput {
  scopesjson?: string;
  filtersjson?: string;
  scopesid?: string;
  filters_fields_to_override?: unknown;
}

export interface GetResourcesVariables {
  first?: number;
  last?: number;
  after?: string;
  before?: string;
  filters_config?: ResourcesFiltersConfigInput;
  sort?: string[];
}

export interface ResourceNode {
  id: string;
  cloud_account?: string;
  datasource_tags_values?: unknown;
  datasources_from_resource_tags?: unknown;
  seem_tags_values?: unknown;
  resource_id?: string;
  cloud_provider?: string;
  cloud_account_friendly_name?: string;
  resource_name?: string;
  resource_type?: string;
  findings_count?: number;
  critical_findings_count?: number;
  open_raw_findings_count?: number;
  // tuple-ish tag values consumed by serializeResourceData in useResources
  seem_tags_key_values?: string[];
  tags_key_values?: string[][];
  resource_attributes?: ResourceAttributeItem[];
}

export interface ResourceEdge {
  node: ResourceNode;
}

export interface ResourcesPageInfo {
  hasNextPage?: boolean;
  endCursor?: string | null;
  startCursor?: string | null;
}

export interface GetResourcesData {
  get_resources: {
    pageInfo: ResourcesPageInfo;
    edges: ResourceEdge[];
  } | null;
}

// --- GET_RESOURCES_COUNT types ---

export interface GetResourcesCountVariables {
  filters_config?: ResourcesFiltersConfigInput;
  filters_context?: string;
}

export interface GetResourcesCountData {
  resources_count: {
    total_count: number;
  } | null;
}
